/**
 * Tour-mode look-around: a drag adds a yaw/pitch offset on top of the waypoint's rotation.
 *
 * Yaw turns about WORLD up and pitch tilts about the camera's own right axis, the way fly and
 * walk look work. Composing both in the camera's local frame (target * euler(pitch, yaw, 0))
 * turned about the camera's tilted up axis instead, so turning left/right at a waypoint that
 * looks down or up banked the horizon.
 */
import * as pc from 'playcanvas';

/** Never tilt past this far up or down (degrees), so a drag can't flip the camera over the top. */
const MAX_ABSOLUTE_PITCH = 89;

const _fwd = new pc.Vec3();
const _yaw = new pc.Quat();
const _pitch = new pc.Quat();

/** Pitch of a rotation's forward (-Z) direction above the horizon, in degrees. */
export function rotationPitchDegrees(rotation: pc.Quat): number {
    rotation.transformVector(pc.Vec3.FORWARD, _fwd);
    return Math.asin(Math.max(-1, Math.min(1, _fwd.y / (_fwd.length() || 1)))) * pc.math.RAD_TO_DEG;
}

/**
 * Clamp a pitch offset so it stays within ±maxOffset of the waypoint's view and the result stays
 * short of straight up/down.
 */
export function clampTourPitch(target: pc.Quat, pitchOffset: number, maxOffset: number): number {
    const base = rotationPitchDegrees(target);
    const lo = Math.max(-maxOffset, -MAX_ABSOLUTE_PITCH - base);
    const hi = Math.min(maxOffset, MAX_ABSOLUTE_PITCH - base);
    return lo > hi ? 0 : Math.max(lo, Math.min(hi, pitchOffset));
}

/** out = yaw about world up · target · pitch about the camera's right axis (degrees). */
export function composeTourLook(target: pc.Quat, yawDeg: number, pitchDeg: number, out: pc.Quat): pc.Quat {
    _yaw.setFromAxisAngle(pc.Vec3.UP, yawDeg);
    _pitch.setFromAxisAngle(pc.Vec3.RIGHT, pitchDeg);
    return out.mul2(_yaw, target).mul(_pitch);
}
